import React, { useRef, useState } from 'react';
import { Paper, Modal, Typography, Box, Grid, Button } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import {
  DIALOG_BOX_STYLE,
  TABLE_HEADER,
} from '../../../../_constants/styleConst';
import {
  GridSortItem,
  GridSortModel,
} from '@mui/x-data-grid/models/gridSortModel';

/** 確認ダイアログで表示するパーツリスト */
export type ConfirmParts = {
  id: number; // DataGridのソート用
  partsCode: string;
  partsName: string;
  amount: number;
  section: string;
  rCode: string;
  rCodeDetail: string;
};

type ConfirmPartsModalProps = {
  isOpen: boolean;
  onClickCancel: () => void;
  onClickOk: () => Promise<void>;
  partsList: ConfirmParts[];
};
const ConfirmPartsModal: React.FC<ConfirmPartsModalProps> = ({
  isOpen,
  onClickCancel,
  onClickOk,
  partsList,
}) => {
  const { t } = useTranslation();
  const [sortModel, setSortModel] = useState<GridSortItem | null>(null);

  // 処理中フラグ
  const isProcessing = useRef(false);

  /** ソート情報を取得する */
  const handleSortChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel.length > 0 ? newSortModel[0] : null);
  };

  /** OKボタン押下 */
  const handleClickOk = async () => {
    if (isProcessing.current) {
      // 連打防止
      return;
    }
    isProcessing.current = true;
    try {
      await onClickOk();
    } finally {
      isProcessing.current = false;
    }
  };

  const style = {
    grid: {
      // ヘッダー
      '& .MuiDataGrid-columnHeaders div[role="row"] .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaders div[role="row"] .MuiDataGrid-filler,  & .MuiDataGrid-columnHeaders div[role="row"] .MuiDataGrid-scrollbarFiller':
        {
          backgroundColor: TABLE_HEADER.backgroundColor,
          color: TABLE_HEADER.color,
          padding: TABLE_HEADER.padding,
        },
      // ヘッダー間の縦線、デフォルトのレイアウト調整列を非表示
      '& .MuiDataGrid-columnSeparator, & .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller':
        {
          display: 'none',
        },
      // 行ホバー時の背景色無効化
      '& .MuiDataGrid-row:hover': {
        backgroundColor: 'inherit',
      },
      // ソートアイコンホバー時の背景色
      '& .MuiDataGrid-columnHeader .MuiDataGrid-sortButton': {
        backgroundColor: TABLE_HEADER.backgroundColor,
      },
      // 基本的にソートアイコンは非表示にする
      '& .MuiDataGrid-columnHeader .MuiDataGrid-sortButton:hover .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-columnHeader:hover .MuiDataGrid-iconButtonContainer':
        {
          visibility: 'hidden',
        },
      // ソートアイコンの色
      '& .MuiDataGrid-iconButtonContainer button > svg': {
        color: 'rgba(255 , 255 , 255 , 1)',
      },
      // ヘッダー固定用
      '&': { maxHeight: `calc(100dvh - 143px)`, overflow: 'auto' },
    },
  };

  /** パーツ未選択時のテンプレート */
  const displayNoRowsMessage: React.FC = () => (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <Typography
        sx={{
          width: '100%',
          textAlign: 'center',
          position: 'absolute',
          top: '30%',
        }}
      >
        {t('MSG_PARTS_SELECTION_NO_PARTS')}
      </Typography>
    </Box>
  );

  /** ヘッダー */
  const columns: GridColDef[] = [
    /** パーツ部番 */
    {
      field: 'partsCode',
      headerName: t('LBL_PARTS_CODE'),
      flex: 1,
      minWidth: 120,
      sortable: true,
      disableColumnMenu: true,
    },
    /** パーツ名称 */
    {
      field: 'partsName',
      headerName: t('LBL_PARTS_NAME'),
      flex: 2,
      sortable: true,
      minWidth: 150,
      disableColumnMenu: true,
    },
    /** 数量 */
    {
      field: 'amount',
      headerName: t('LBL_AMOUNT'),
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 70,
      sortable: false,
      disableColumnMenu: true,
    },
    /** セクション */
    {
      field: 'section',
      headerName: t('LBL_SECTION'),
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 120,
      sortable: true,
      disableColumnMenu: true,
    },
    /** Rコード */
    {
      field: 'rCode',
      headerName: t('LBL_R_CODE'),
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 80,
      sortable: true,
      disableColumnMenu: true,
    },
    /** Rコード 詳細 */
    {
      field: 'rCodeDetail',
      headerName: t('LBL_R_DETAIL'),
      flex: 2,
      minWidth: 150,
      sortable: true,
      disableColumnMenu: true,
    },
  ];

  return (
    <Modal
      open={isOpen}
      onClose={onClickCancel}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box
        sx={{
          ...DIALOG_BOX_STYLE,
          width: { xs: '90%', xl: '70%' },
          maxWidth: 'none',
          height: 'auto',
          maxHeight: '98%',
          bgcolor: 'background.default',
          overflow: 'auto',
          p: 2,
        }}
      >
        <Typography
          id="modal-title"
          variant="h6"
          component="h2"
          sx={{ whiteSpace: 'pre-line', mb: '0.25rem' }}
        >
          {t('MSG_SELECTION')}
        </Typography>
        <Box
          sx={{
            width: '100%',
          }}
        >
          <Paper>
            <DataGrid
              rows={partsList}
              rowHeight={33}
              columns={columns}
              pageSizeOptions={[5, 10, 100]}
              sx={[
                style.grid,
                {
                  /** ソート中のヘッダーはホバー時にソートアイコンを表示する */
                  [`& .MuiDataGrid-columnHeader[data-field="${sortModel?.field}"] .MuiDataGrid-sortButton:hover, & .MuiDataGrid-columnHeader[data-field="${sortModel?.field}"]:hover .MuiDataGrid-iconButtonContainer`]:
                    {
                      visibility: 'visible',
                    },
                },
              ]}
              autosizeOptions={{
                includeOutliers: true,
              }}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 100 },
                },
              }}
              slots={{
                noRowsOverlay: displayNoRowsMessage,
              }}
              hideFooter={partsList.length < 100}
              onSortModelChange={(model) => {
                handleSortChange(model);
              }}
            />
          </Paper>
        </Box>
        <Box>
          <Grid container spacing={2} sx={{ mt: '1rem' }}>
            <Grid size={{ xs: 6, md: 8 }}></Grid>
            <Grid size={{ xs: 3, md: 2 }}>
              <Button onClick={onClickCancel} variant="outlined" fullWidth>
                {t('BTN_CANCEL')}
              </Button>
            </Grid>
            <Grid size={{ xs: 3, md: 2 }}>
              <Button
                onClick={() => handleClickOk()}
                variant="contained"
                fullWidth
                disabled={isProcessing.current}
              >
                {t('BTN_OK')}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Modal>
  );
};

export default ConfirmPartsModal;
